import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { toast } from 'react-toastify';

import {
    useCreateDiscountMutation,
    useUpdateDiscountMutation,
} from '../../slices/discountApiSlice';

import 'react-datepicker/dist/react-datepicker.css';

const globalReasons = new Set(['welcome', 'holiday']);

const defaultForm = {
    code: '',
    type: 'percentage',
    value: '',
    minPurchaseAmount: '',
    expiresAt: null,
    companyRuc: '',
    usageLimit: 1,
    onePerUser: false,
    isGlobal: false,
    reason: '',
};

const parseBoolean = (value) => value === true || value === 'true' || value === 1 || value === '1';

const parseDate = (value) => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const DiscountFormModal = ({ show, handleClose, initialData = null, onSuccess }) => {
    const companiesByRuc = useSelector((state) => state.companies?.companiesByRuc || {});
    const [createDiscount, { isLoading: creatingDiscount, error: createError }] = useCreateDiscountMutation();
    const [updateDiscount, { isLoading: updatingDiscount, error: updateError }] = useUpdateDiscountMutation();

    const [formData, setFormData] = useState(defaultForm);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isEditing = Boolean(initialData?._id);

    const companyOptions = useMemo(
        () => Object.values(companiesByRuc || {}),
        [companiesByRuc]
    );

    useEffect(() => {
        if (!show) return;

        setFormData({
            code: initialData?.code || '',
            type: initialData?.type || 'percentage',
            value: initialData?.value ?? '',
            minPurchaseAmount: initialData?.minPurchaseAmount ?? '',
            expiresAt: parseDate(initialData?.expiresAt),
            companyRuc: initialData?.companyRuc || '',
            usageLimit: initialData?.usageLimit ?? 1,
            onePerUser: parseBoolean(initialData?.onePerUser),
            isGlobal: parseBoolean(initialData?.isGlobal),
            reason: globalReasons.has(initialData?.reason) ? initialData.reason : '',
        });
    }, [show, initialData]);

    const onFieldChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const onGlobalDiscountChange = (checked) => {
        setFormData((prev) => ({
            ...prev,
            isGlobal: checked,
            onePerUser: checked ? true : prev.onePerUser,
            reason: checked ? prev.reason : '',
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const isGlobal = Boolean(formData.isGlobal);
        const reason = globalReasons.has(formData.reason) ? formData.reason : '';
        const payload = {
            code: String(formData.code || '').trim(),
            type: formData.type || 'percentage',
            value: Number(formData.value),
            minPurchaseAmount: Number(formData.minPurchaseAmount),
            expiresAt: formData.expiresAt ? formData.expiresAt.toISOString() : null,
            companyRuc: String(formData.companyRuc || '').trim(),
            usageLimit: Number(formData.usageLimit),
            onePerUser: isGlobal ? true : Boolean(formData.onePerUser),
            isActive: true,
            isGlobal,
        };

        if (!payload.code || !payload.companyRuc) {
            toast.error('Completa codigo y empresa para continuar.');
            return;
        }

        if (isGlobal) {
            if (!reason) {
                toast.error('Selecciona la razón del descuento global.');
                return;
            }
            payload.reason = reason;
        }

        setIsSubmitting(true);
        try {
            if (isEditing) {
                await updateDiscount({ id: initialData._id, data: payload }).unwrap();
                toast.success('Descuento actualizado correctamente.');
            } else {
                await createDiscount(payload).unwrap();
                toast.success('Descuento creado correctamente.');
            }

            if (onSuccess) {
                await onSuccess();
            }
            handleClose();
        } catch (error) {
            toast.error(error?.data?.message || error?.error || 'No se pudo guardar el descuento.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const discountError = createError?.data?.message || updateError?.data?.message || createError?.error || updateError?.error;
    const disabled = isSubmitting || creatingDiscount || updatingDiscount;

    return (
        <Modal show={show} onHide={handleClose} centered size="lg" className="discount-form-modal">
            <Modal.Header closeButton style={{ borderBottomColor: '#f2d6cb' }}>
                <Modal.Title style={{ color: '#b5430f' }}>
                    {isEditing ? 'Editar descuento' : 'Crear descuento'}
                </Modal.Title>
            </Modal.Header>

            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {discountError ? <Alert variant="danger">{discountError}</Alert> : null}

                    <Row className="g-3">
                        <Col xs={12} md={6}>
                            <Form.Group controlId="discount-code">
                                <Form.Label column={false}>Código</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.code}
                                    onChange={(e) => onFieldChange('code', e.target.value)}
                                    placeholder="Ej: BIENVENIDA10"
                                    required
                                    maxLength={40}
                                    disabled={disabled}
                                />
                            </Form.Group>
                        </Col>

                        <Col xs={12} md={6}>
                            <Form.Group controlId="discount-type">
                                <Form.Label column={false}>Tipo</Form.Label>
                                <Form.Select
                                    value={formData.type}
                                    onChange={(e) => onFieldChange('type', e.target.value)}
                                    disabled={disabled}
                                >
                                    <option value="percentage">Porcentaje</option>
                                    <option value="fixed">Fijo</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col xs={12} md={6}>
                            <Form.Group controlId="discount-value">
                                <Form.Label column={false}>Valor</Form.Label>
                                <Form.Control
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    value={formData.value}
                                    onChange={(e) => onFieldChange('value', e.target.value)}
                                    required
                                    disabled={disabled}
                                />
                            </Form.Group>
                        </Col>

                        <Col xs={12} md={6}>
                            <Form.Group controlId="discount-min-purchase">
                                <Form.Label column={false}>Compra mínima</Form.Label>
                                <Form.Control
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    value={formData.minPurchaseAmount}
                                    onChange={(e) => onFieldChange('minPurchaseAmount', e.target.value)}
                                    required
                                    disabled={disabled}
                                />
                            </Form.Group>
                        </Col>

                        <Col xs={12} md={6}>
                            <Form.Group controlId="discount-expires-at">
                                <Form.Label column={false}>Fecha de expiración</Form.Label>
                                <DatePicker
                                    selected={formData.expiresAt}
                                    onChange={(date) => onFieldChange('expiresAt', date)}
                                    dateFormat="dd/MM/yyyy"
                                    className="form-control"
                                    placeholderText="Seleccionar fecha..."
                                    isClearable
                                    minDate={new Date()}
                                    todayButton="Hoy"
                                    showPopperArrow={false}
                                    popperClassName="discount-datepicker-popper"
                                    popperPlacement="bottom-start"
                                    portalId="root"
                                    calendarClassName="custom-calendar"
                                    showMonthYearDropdown={false}
                                    disabled={disabled}
                                />
                            </Form.Group>
                        </Col>

                        <Col xs={12} md={6}>
                            <Form.Group controlId="discount-company-ruc">
                                <Form.Label column={false}>RUC de empresa</Form.Label>
                                {companyOptions.length > 0 ? (
                                    <Form.Select
                                        value={formData.companyRuc}
                                        onChange={(e) => onFieldChange('companyRuc', e.target.value)}
                                        required
                                        disabled={disabled}
                                    >
                                        <option value="">Seleccionar empresa...</option>
                                        {companyOptions.map((company) => (
                                            <option key={company.ruc} value={company.ruc}>
                                                {(company.tradeName || company.legalName || 'Empresa')} - {company.ruc}
                                            </option>
                                        ))}
                                    </Form.Select>
                                ) : (
                                    <Form.Control
                                        type="text"
                                        value={formData.companyRuc}
                                        onChange={(e) => onFieldChange('companyRuc', e.target.value)}
                                        placeholder="Ingrese el RUC"
                                        required
                                        disabled={disabled}
                                    />
                                )}
                            </Form.Group>
                        </Col>

                        <Col xs={12} md={6}>
                            <Form.Group controlId="discount-usage-limit">
                                <Form.Label column={false}>Límite de uso</Form.Label>
                                <Form.Control
                                    type="number"
                                    min={1}
                                    step="1"
                                    value={formData.usageLimit}
                                    onChange={(e) => onFieldChange('usageLimit', e.target.value)}
                                    required
                                    disabled={disabled}
                                />
                            </Form.Group>
                        </Col>

                        <Col xs={12}>
                            <Form.Group controlId="discount-one-per-user">
                                <div
                                    className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 rounded-3 p-3"
                                    style={{ backgroundColor: '#fff5f2', border: '1px solid #f2d6cb' }}
                                >
                                    <div>
                                        <Form.Label column={false} className="fw-semibold mb-1">Uso único por usuario</Form.Label>
                                        <Form.Text className="d-block text-muted">
                                            {formData.isGlobal
                                                ? 'Los descuentos globales son de un solo uso por usuario.'
                                                : 'Limita este descuento a un solo canje por usuario.'}
                                        </Form.Text>
                                    </div>

                                    <Form.Check
                                        id="discount-one-per-user-switch"
                                        type="switch"
                                        label={formData.onePerUser ? 'Activado' : 'Desactivado'}
                                        checked={Boolean(formData.onePerUser)}
                                        onChange={(e) => onFieldChange('onePerUser', e.target.checked)}
                                        disabled={disabled || formData.isGlobal}
                                        className="mb-0"
                                    />
                                </div>
                            </Form.Group>
                        </Col>

                        <Col xs={12}>
                            <Form.Group controlId="discount-is-global">
                                <div
                                    className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 rounded-3 p-3"
                                    style={{ backgroundColor: '#fff5f2', border: '1px solid #f2d6cb' }}
                                >
                                    <div>
                                        <Form.Label column={false} className="fw-semibold mb-1">Descuento global</Form.Label>
                                        <Form.Text className="d-block text-muted">
                                            Se ofrece automáticamente. El cliente ve solo el mejor descuento global que aún no ha usado.
                                        </Form.Text>
                                    </div>

                                    <Form.Check
                                        id="discount-is-global-switch"
                                        type="switch"
                                        label={formData.isGlobal ? 'Activado' : 'Desactivado'}
                                        checked={Boolean(formData.isGlobal)}
                                        onChange={(e) => onGlobalDiscountChange(e.target.checked)}
                                        disabled={disabled}
                                        className="mb-0"
                                    />
                                </div>
                            </Form.Group>
                        </Col>

                        {formData.isGlobal ? (
                            <Col xs={12}>
                                <Form.Group controlId="discount-global-reason">
                                    <div
                                        className="rounded-3 p-3"
                                        style={{ backgroundColor: '#fff5f2', border: '1px solid #f2d6cb' }}
                                    >
                                        <Form.Label column={false} className="fw-semibold mb-1">Razón</Form.Label>
                                        <Form.Select
                                            value={formData.reason}
                                            onChange={(e) => onFieldChange('reason', e.target.value)}
                                            required
                                            disabled={disabled}
                                            onInvalid={(event) => {
                                                event.preventDefault();
                                                toast.error('Selecciona la razón del descuento global.');
                                            }}
                                        >
                                            <option value="">Seleccionar razón...</option>
                                            <option value="welcome">Bienvenida</option>
                                            <option value="holiday">Feriado</option>
                                        </Form.Select>
                                    </div>
                                </Form.Group>
                            </Col>
                        ) : null}
                    </Row>
                </Modal.Body>

                <Modal.Footer className="d-flex justify-content-between">
                    <Button variant="outline-secondary" onClick={handleClose} disabled={disabled}>
                        Cancelar
                    </Button>
                    <Button type="submit" disabled={disabled} style={{ backgroundColor: '#b5430f', borderColor: '#b5430f' }}>
                        {disabled ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Guardando...
                            </>
                        ) : isEditing ? (
                            'Actualizar descuento'
                        ) : (
                            'Crear descuento'
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default DiscountFormModal;
